import { useBoolean } from '@wegroup/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';
import useBackRef from '../../../../common/src/hooks/query-params/useBackRef';
import usePartyId from '../../../../common/src/hooks/query-params/usePartyId';
import useQueryParams from '../../../../common/src/hooks/useQueryParams';
import useUuid from '../../../../common/src/hooks/useUuid';
import { useCompanyScanDispatch } from '../../../../common/src/utils/globalVariables';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import usePreviousSession from '../../../hooks/usePreviousSession';
import useStartCompanyScanFlow from '../../../hooks/useStartCompanyScanFlow';
import { setPartyId } from '../../../store/redux/slices/companyScanSlice';

interface HandleNavigation {
  handleOnPrevious: (() => void) | undefined;
  handleOnNext: () => void;
  nextText: string;
  previousText: string;
  isLoading: boolean;
}

const useHandleIntroNavigation = (): HandleNavigation => {
  const { t } = useTranslation();
  const history = useHistory();
  const companyScanDispatch = useCompanyScanDispatch();

  // Query params
  const backRef = useBackRef();
  const partyId = usePartyId();
  const sessionId = useSessionId();
  const queryParams = useQueryParams();

  // State
  const [isStarting, setIsStarting] = useBoolean();

  // Queries
  const { data: previousSession, isLoading: isPreviousSessionLoading } =
    usePreviousSession(partyId);

  // Flows hooks
  const startFlow = useStartCompanyScanFlow();
  const uuid = useUuid();

  // Effects
  useEffect(() => {
    if (partyId) companyScanDispatch(setPartyId(partyId));
  }, [partyId]);

  // Functions
  const handleOnNext = () => {
    setIsStarting.on();
    const flowId = queryParams.get('flowId') || undefined;
    const _sessionId = sessionId || previousSession?.id;

    if (!sessionId) {
      startFlow(undefined, partyId, undefined, flowId);
    } else if (_sessionId) {
      startFlow(_sessionId, undefined, undefined, flowId);
    } else {
      startFlow(uuid, undefined, undefined, flowId);
    }

    setIsStarting.off();
  };

  const handleOnPrevious = () => {
    if (backRef) history.push(backRef);
    else if (partyId) history.push(`/conversation?party_id=${partyId}`);
    else history.push(`/conversation`);
  };

  const isLoading = isPreviousSessionLoading || isStarting;

  const getNextText = () => {
    if (isSocialLoginUrl)
      return t('COMPANY-SCAN_CBE-LOOKUP-EXISTING_BUTTON_TO-QUESTIONS');
    else if (previousSession || sessionId)
      return t('COMPANY-SCAN_CBE-LOOKUP-EXISTING_BUTTON_RESUME-PREVIOUS');
    return t('COMPANY-SCAN_CBE-LOOKUP-EXISTING_BUTTON_START-NEW');
  };

  const getPreviousText = () => {
    if (backRef) return t('PREVIOUS');
    return t('BACK-TO-DASHBOARD');
  };

  return {
    handleOnPrevious:
      !isSocialLoginUrl || backRef ? handleOnPrevious : undefined,
    handleOnNext,
    isLoading,
    nextText: getNextText(),
    previousText: getPreviousText(),
  };
};

export default useHandleIntroNavigation;
